<template>
    <div class="callback-container">
        <div class="registration-message">
            <h2 v-if="success">{{ isCustomBotFlow ? 'Bot personnalisé connecté !' : 'Connexion Twitch réussie !' }}</h2>
            <h2 v-else-if="error">Erreur de connexion Twitch</h2>
            <h2 v-else>Traitement en cours...</h2>
            
            <div v-if="success && isCustomBotFlow">
                <p>Le compte bot Twitch a été lié à votre abonnement.</p>
                <p>Vous pouvez retourner sur le dashboard pour terminer la configuration.</p>
                <p>Veuillez cliquer sur le bouton ci-dessous pour fermer cette fenêtre.</p>
            </div>

            <div v-else-if="success">
                <p>Vous êtes maintenant connecté avec Twitch.</p>
                <p>Les données sont en cours de transmission à la fenêtre principale.</p>
                <p>Veuillez cliquer sur le bouton ci-dessous pour fermer cette fenêtre.</p>
            </div>
            
            <div v-else-if="error">
                <p>Une erreur s'est produite lors de la connexion avec Twitch:</p>
                <p class="error-message">{{ errorMessage }}</p>
            </div>
            
            <div v-else>
                <p>Veuillez patienter pendant que nous traitons votre authentification...</p>
                <div class="loading-spinner"></div>
            </div>
            
            <button @click="closeWindow" class="close-button">Fermer cette fenêtre</button>
        </div>
    </div>
</template>

<script>
import { subscriptionService } from '../services/subscription'

export default {
    name: 'TwitchCallback',
    data() {
        return {
            success: false,
            error: false,
            errorMessage: '',
            code: null,
            state: null,
            userData: null,
            loading: true,
            isCustomBotFlow: false
        }
    },
    methods: {
        closeWindow() {
            window.close();
        },
        // Stocker le code et l'état dans le localStorage
        storeInLocalStorage(code, state) {
            //console.log('Stockage dans localStorage:', { code, state });
            try {
                localStorage.setItem('twitch_auth_code', code);
                localStorage.setItem('twitch_auth_state_received', state);
                //console.log('Données stockées avec succès dans localStorage');
                return true;
            } catch (e) {
                console.error('Erreur lors du stockage dans localStorage:', e);
                return false;
            }
        },
        // Envoyer les données à la fenêtre principale
        sendToParentWindow(data) {
            //console.log('Envoi des données à la fenêtre principale:', data);
            try {
                if (window.opener) {
                    window.opener.postMessage(data, '*');
                    //console.log('Données envoyées avec succès à la fenêtre principale');
                    return true;
                } else {
                    console.error('Fenêtre principale non disponible');
                    return false;
                }
            } catch (e) {
                console.error('Erreur lors de l\'envoi des données à la fenêtre principale:', e);
                return false;
            }
        },
        isCustomBotState(state) {
            return typeof state === 'string' && state.startsWith('custom_bot_');
        },
        async completeCustomBotFlow() {
            try {
                const defaultRedirectUri = window.location.origin + '/twitch-callback';
                const redirectUri = process.env.VUE_APP_TWITCH_REDIRECT_URI || defaultRedirectUri;

                const response = await subscriptionService.completeCustomBotOAuth(
                    this.code,
                    this.state,
                    redirectUri
                );

                if (!response?.data?.success) {
                    throw new Error(response?.data?.error?.message || 'Erreur inconnue');
                }

                this.success = true;
                this.error = false;
            } catch (error) {
                this.error = true;
                this.success = false;
                this.errorMessage = error?.response?.data?.error?.message || error?.message || 'Échec de la connexion du bot personnalisé';
            } finally {
                this.loading = false;
            }
        }
    },
    async mounted() {
        //console.log('TwitchCallback monté, analyse des paramètres URL...');
        this.loading = true;
        
        // Récupérer le code d'autorisation et l'état de l'URL
        const urlParams = new URLSearchParams(window.location.search);
        this.code = urlParams.get('code');
        this.state = urlParams.get('state');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        
        if (error) {
            // En cas d'erreur
            this.error = true;
            this.errorMessage = errorDescription || error;
            this.loading = false;
            
            // Envoyer l'erreur à la fenêtre principale
            this.sendToParentWindow({
                type: 'twitch_auth_error',
                error,
                errorDescription
            });
            
            // Stocker également l'erreur dans le localStorage comme solution de secours
            localStorage.setItem('twitch_auth_error', error);
            localStorage.setItem('twitch_auth_error_description', errorDescription || '');
        } else if (this.code && this.state) {
            if (this.isCustomBotState(this.state)) {
                this.isCustomBotFlow = true;
                await this.completeCustomBotFlow();
                return;
            }

            // En cas de succès, simplement stocker le code et l'état
            //console.log('Code Twitch reçu, transmission au composant parent');
            this.success = true;
            this.loading = false;
            
            // Double approche: localStorage et postMessage
            this.storeInLocalStorage(this.code, this.state);
            
            // Envoyer le code et l'état à la fenêtre principale
            // Le backend se chargera de l'échange du code contre un token
            this.sendToParentWindow({
                type: 'twitch_auth_success',
                code: this.code,
                state: this.state
            });
        } else {
            console.warn('Aucun code ou état trouvé dans l\'URL');
            this.loading = false;
        }
    }
}
</script>

<style scoped>
.callback-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background: linear-gradient(135deg, #6441a5 0%, #9146FF 100%);
    color: white;
    padding: 20px;
}

.registration-message {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 30px;
    text-align: center;
    max-width: 500px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.registration-message h2 {
    color: white;
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 1.8rem;
}

.registration-message p {
    margin-bottom: 15px;
    font-size: 1.1rem;
    line-height: 1.5;
}

.close-button {
    background-color: #9146FF;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 20px;
}

.close-button:hover {
    background-color: #7D3FD3;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.error-message {
    color: #ff6b6b;
    font-weight: bold;
    background: rgba(255, 107, 107, 0.1);
    padding: 10px;
    border-radius: 6px;
    margin: 15px 0;
    border-left: 3px solid #ff6b6b;
}

.loading-spinner {
    display: inline-block;
    width: 40px;
    height: 40px;
    margin: 15px auto;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: #eda526;
    animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
